import { Injectable } from '@angular/core';

type QualityMetadata = Record<string, string | number | boolean | null>;

interface QualityEvent {
  name: string;
  at: number;
  value?: number;
  metadata?: QualityMetadata;
}

interface QualityStore {
  version: 1;
  sessions: number;
  healthySessions: number;
  deepLinks: number;
  successfulDeepLinks: number;
  events: QualityEvent[];
  samples: Record<string, number[]>;
}

export interface ReleaseGateSnapshot {
  jsErrorFreeSessionsPercent: number | null;
  coldStartP75Ms: number | null;
  feedFirstContentP75Ms: number | null;
  interactionP75Ms: number | null;
  deepLinkSuccessPercent: number | null;
  feedFpsP75: number | null;
  feedFpsP25: number | null;
  targets: {
    crashFreeSessionsPercent: number;
    coldStartP75Ms: number;
    feedFirstContentP75Ms: number;
    interactionP75Ms: number;
    deepLinkSuccessPercent: number;
    feedFps: number;
  };
}

/**
 * Lightweight, privacy-safe release-gate telemetry.
 *
 * Measurements stay on-device for now and are also emitted as a DOM event so
 * a production analytics/crash adapter can forward them later without making
 * startup depend on a third-party SDK. Route values and error messages are
 * deliberately excluded.
 */
@Injectable({ providedIn: 'root' })
export class AppQualityService {
  private readonly storageKey = 'laniakea.quality.v1';
  private readonly maxEvents = 160;
  private readonly maxSamplesPerMetric = 80;
  private readonly navigationStarts = new Map<number, number>();
  private readonly surfaceStarts = new Map<string, number>();
  private readonly reportedSurfaces = new Set<string>();
  private readonly activeFrameSamples = new Set<string>();
  private initialized = false;
  private sessionHealthy = true;
  private appInteractiveRecorded = false;
  private store: QualityStore = this.emptyStore();

  readonly targets = {
    crashFreeSessionsPercent: 99.95,
    coldStartP75Ms: 2000,
    feedFirstContentP75Ms: 1500,
    interactionP75Ms: 200,
    deepLinkSuccessPercent: 99.9,
    feedFps: 60,
  };

  initialize(): void {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;
    this.store = this.readStore();
    this.store.sessions += 1;
    this.store.healthySessions += 1;
    this.persist();

    window.addEventListener('error', event => {
      this.markSessionUnhealthy('error', this.errorKind(event.error));
    });
    window.addEventListener('unhandledrejection', event => {
      this.markSessionUnhealthy('unhandledrejection', this.errorKind(event.reason));
    });

    this.observeLongTasks();
    this.observeSlowInteractions();
    this.installNetworkTracking();
    this.record('session_started', undefined, {
      platform: this.platformName(),
    });

    // A deliberately read-only hook for local QA and release automation.
    // It contains aggregate timing data only; routes, payloads and identities
    // are never exposed here.
    (window as any).__laniakeaQuality = () => this.getReleaseGateSnapshot();
  }

  navigationStarted(id: number, url: string): void {
    this.navigationStarts.set(id, this.now());
    this.record('navigation_started', undefined, { route: this.sanitizeRoute(url) });
  }

  navigationCompleted(id: number, url: string, success: boolean, error?: unknown): void {
    const startedAt = this.navigationStarts.get(id);
    this.navigationStarts.delete(id);
    const duration = startedAt === undefined ? undefined : Math.max(0, this.now() - startedAt);
    this.record(success ? 'navigation_completed' : 'navigation_failed', duration, {
      route: this.sanitizeRoute(url),
      ...(success ? {} : { errorKind: this.errorKind(error) }),
    });
  }

  markAppInteractive(route: string): void {
    if (this.appInteractiveRecorded) return;
    this.appInteractiveRecorded = true;
    const bootStartedAt = Number((window as any).__laniakeaBootStartedAt);
    const duration = Number.isFinite(bootStartedAt)
      ? Math.max(0, this.now() - bootStartedAt)
      : undefined;
    this.record('app_interactive', duration, { route: this.sanitizeRoute(route) });
  }

  beginSurfaceLoad(surface: string): void {
    const key = this.safeLabel(surface);
    this.surfaceStarts.set(key, this.now());
    this.reportedSurfaces.delete(key);
  }

  markFeedFirstContent(surface: string, source: 'cache' | 'network' | 'existing' = 'network'): void {
    const key = this.safeLabel(surface);
    if (this.reportedSurfaces.has(key)) return;
    this.reportedSurfaces.add(key);
    const startedAt = this.surfaceStarts.get(key);
    const duration = startedAt === undefined ? undefined : Math.max(0, this.now() - startedAt);
    this.record('feed_first_content', duration, { surface: key, source });
  }

  markInteraction(name: string, startedAt: number): number {
    const duration = Math.max(0, this.now() - startedAt);
    this.record('interaction_response', duration, { interaction: this.safeLabel(name) });
    return duration;
  }

  beginDeepLink(source: string, target: string): string {
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    this.store.deepLinks += 1;
    this.record('deep_link_started', undefined, {
      id,
      source: this.safeLabel(source),
      route: this.sanitizeRoute(target),
    });
    return id;
  }

  finishDeepLink(id: string, source: string, target: string, success: boolean, startedAt: number): void {
    if (success) this.store.successfulDeepLinks += 1;
    this.record(success ? 'deep_link_completed' : 'deep_link_failed', Math.max(0, this.now() - startedAt), {
      id,
      source: this.safeLabel(source),
      route: this.sanitizeRoute(target),
    });
  }

  startFrameSample(label: string, durationMs = 750): void {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function' || document.hidden) return;
    const key = this.safeLabel(label);
    if (this.activeFrameSamples.has(key)) return;
    this.activeFrameSamples.add(key);

    const startedAt = this.now();
    let previousFrameAt = startedAt;
    let frames = 0;
    let slowFrames = 0;
    const sample = (timestamp: number): void => {
      if (document.hidden) {
        this.activeFrameSamples.delete(key);
        return;
      }
      frames += 1;
      if (timestamp - previousFrameAt > 34) slowFrames += 1;
      previousFrameAt = timestamp;
      const elapsed = timestamp - startedAt;
      if (elapsed < durationMs) {
        window.requestAnimationFrame(sample);
        return;
      }

      this.activeFrameSamples.delete(key);
      // There is no interval before the first callback, hence frames - 1.
      const fps = elapsed > 0 ? Math.min(60, Math.max(0, ((frames - 1) * 1000) / elapsed)) : 60;
      this.record('frame_sample', fps, { surface: key, slowFrames });
    };
    window.requestAnimationFrame(sample);
  }

  getReleaseGateSnapshot(): ReleaseGateSnapshot {
    return {
      // Native crash-free sessions still need MetricKit/Crashlytics. This is
      // the immediately useful JavaScript error-free proxy.
      jsErrorFreeSessionsPercent: this.store.sessions
        ? (this.store.healthySessions / this.store.sessions) * 100
        : null,
      coldStartP75Ms: this.percentile(this.store.samples['app_interactive'], 0.75),
      feedFirstContentP75Ms: this.percentile(this.store.samples['feed_first_content'], 0.75),
      interactionP75Ms: this.percentile(this.store.samples['interaction_response'], 0.75),
      deepLinkSuccessPercent: this.store.deepLinks
        ? (this.store.successfulDeepLinks / this.store.deepLinks) * 100
        : null,
      feedFpsP75: this.percentile(this.store.samples['frame_sample'], 0.75),
      // Frame rate is a higher-is-better metric, so its lower quartile is the
      // useful release signal. P75 remains available for trend comparison.
      feedFpsP25: this.percentile(this.store.samples['frame_sample'], 0.25),
      targets: this.targets,
    };
  }

  private markSessionUnhealthy(source: string, errorKind: string): void {
    if (this.sessionHealthy) {
      this.sessionHealthy = false;
      this.store.healthySessions = Math.max(0, this.store.healthySessions - 1);
    }
    this.record('runtime_error', undefined, { source, errorKind });
  }

  private observeLongTasks(): void {
    if (typeof PerformanceObserver === 'undefined') return;
    try {
      const observer = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          this.record('long_task', entry.duration, { entryType: entry.entryType });
        });
      });
      observer.observe({ entryTypes: ['longtask'] });
    } catch {
      // Older WKWebViews do not expose the long-task entry type.
    }
  }

  private observeSlowInteractions(): void {
    if (typeof PerformanceObserver === 'undefined') return;
    try {
      const observer = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          if (entry.duration >= this.targets.interactionP75Ms) {
            this.record('slow_browser_interaction', entry.duration, { entryType: entry.entryType });
          }
        });
      });
      observer.observe({ type: 'event', buffered: true, durationThreshold: 16 } as PerformanceObserverInit);
    } catch {
      // Event Timing is not available in every supported WebView.
    }
  }

  private installNetworkTracking(): void {
    const report = (): void => {
      this.record('network_state_changed', undefined, {
        online: typeof navigator === 'undefined' ? true : navigator.onLine,
      });
    };
    window.addEventListener('online', report);
    window.addEventListener('offline', report);
    report();
  }

  private record(name: string, value?: number, metadata?: QualityMetadata): void {
    const event: QualityEvent = {
      name,
      at: Date.now(),
      ...(Number.isFinite(value) ? { value: Number(value) } : {}),
      ...(metadata ? { metadata } : {}),
    };
    this.store.events.push(event);
    if (this.store.events.length > this.maxEvents) {
      this.store.events.splice(0, this.store.events.length - this.maxEvents);
    }
    if (Number.isFinite(value)) {
      const values = this.store.samples[name] || [];
      values.push(Number(value));
      if (values.length > this.maxSamplesPerMetric) values.splice(0, values.length - this.maxSamplesPerMetric);
      this.store.samples[name] = values;
    }
    this.persist();

    try {
      window.dispatchEvent(new CustomEvent('laniakea:quality-event', { detail: event }));
    } catch {
      // Diagnostics must never affect the product path they measure.
    }
  }

  private readStore(): QualityStore {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.storageKey) || 'null') as QualityStore | null;
      if (parsed?.version === 1 && Array.isArray(parsed.events) && parsed.samples) return parsed;
    } catch {
      // Storage can be unavailable in private browsing or a hardened WebView.
    }
    return this.emptyStore();
  }

  private persist(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.store));
    } catch {
      // Quality telemetry is best-effort and must not block startup.
    }
  }

  private emptyStore(): QualityStore {
    return {
      version: 1,
      sessions: 0,
      healthySessions: 0,
      deepLinks: 0,
      successfulDeepLinks: 0,
      events: [],
      samples: {},
    };
  }

  private percentile(values: number[] | undefined, percentile: number): number | null {
    if (!values?.length) return null;
    const sorted = [...values].filter(Number.isFinite).sort((a, b) => a - b);
    if (!sorted.length) return null;
    const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(sorted.length * percentile) - 1));
    return Number(sorted[index].toFixed(2));
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  private safeLabel(value: string): string {
    return String(value || 'unknown').replace(/[^a-zA-Z0-9_.:-]/g, '_').slice(0, 80);
  }

  private platformName(): string {
    const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
    if (/Android/i.test(userAgent)) return 'android';
    return 'web';
  }

  private errorKind(error: unknown): string {
    if (error instanceof Error) return this.safeLabel(error.name || 'Error');
    return this.safeLabel(typeof error);
  }

  private sanitizeRoute(rawUrl: string): string {
    try {
      const parsed = new URL(rawUrl || '/', 'https://laniakea.tv');
      const keys = Array.from(new Set(Array.from(parsed.searchParams.keys()))).sort();
      return `${parsed.pathname || '/'}${keys.length ? `?${keys.join('&')}` : ''}`.slice(0, 180);
    } catch {
      return '/invalid-route';
    }
  }
}
